import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from '../../axios/requests';
import API from '../../constant.api';

export interface Purity {
  _id: string;
  purityName: string;
  purityPercentage: number;
}

export interface MetalRecord {
  _id: string;
  name: string;
  price?: number;
  salePrice?: number;
  // sorted by percentage, highest first
  purityQuality: Purity[];
  types: string[];
}

export interface MetalChangeResponse {
  message: string;
  // the full updated record: replace it in state
  metal: MetalRecord;
}

// Superadmin only. requests.ts attaches the Bearer token.
const enc = encodeURIComponent;

export const fetchMetals = async (): Promise<MetalRecord[]> => {
  const { data } = await getRequest(API.METALS, {});
  return data;
};

// A metal that is missing (e.g. Silver when only Gold was set up). It needs at least one purity and one type:
// GET /metals only lists metals that have a purity. The prices start at 0 and are set on the Overview page.
export const createMetal = async (payload: {
  name: string;
  price: number;
  salePrice: number;
  purityQuality: Array<{ purityName: string; purityPercentage: number }>;
  types: string[];
}): Promise<MetalRecord> => {
  const { data } = await postRequest(API.METALS, payload);
  return data;
};

// ---- purities
export const addPurity = async (
  metalId: string,
  payload: { purityName: string; purityPercentage: number }
): Promise<MetalChangeResponse> => {
  const { data } = await postRequest(
    `${API.METALS}/${enc(metalId)}/purities`,
    payload
  );
  return data;
};

export const updatePurity = async (
  metalId: string,
  purityId: string,
  payload: { purityName?: string; purityPercentage?: number }
): Promise<MetalChangeResponse> => {
  const { data } = await putRequest(
    `${API.METALS}/${enc(metalId)}/purities/${enc(purityId)}`,
    payload
  );
  return data;
};

export const deletePurity = async (
  metalId: string,
  purityId: string
): Promise<MetalChangeResponse> => {
  const { data } = await deleteRequest(
    `${API.METALS}/${enc(metalId)}/purities/${enc(purityId)}`
  );
  return data;
};

// ---- types (:type is the CURRENT text; it can contain spaces, &, commas and brackets)
export const addType = async (
  metalId: string,
  type: string
): Promise<MetalChangeResponse> => {
  const { data } = await postRequest(`${API.METALS}/${enc(metalId)}/types`, {
    type,
  });
  return data;
};

export const updateType = async (
  metalId: string,
  type: string,
  newType: string
): Promise<MetalChangeResponse> => {
  const { data } = await putRequest(
    `${API.METALS}/${enc(metalId)}/types/${enc(type)}`,
    { newType }
  );
  return data;
};

export const deleteType = async (
  metalId: string,
  type: string
): Promise<MetalChangeResponse> => {
  const { data } = await deleteRequest(
    `${API.METALS}/${enc(metalId)}/types/${enc(type)}`
  );
  return data;
};
